import React, { forwardRef, ReactNode } from "react";

import { bemClassNames, CUSTOM_CSS_PROPERTY_PREFIX, transformKeysToParamCase } from "@multiweb/reactUtils";
import { ForwardRefType } from "@multiweb/types/react";

/**
 * Počet sloupců GridLayoutu, které bude komponenta zabírat
 */
export enum ColSpan {
    one = 1,
    two,
    three,
    four,
    five,
    six,
    seven,
    eight,
    nine,
    ten,
    eleven,
    twelve,
}

/** Horizontální zarovnání komponenty */
export enum AlignSelf {
    start = "start",
    end = "end",
    center = "center",
    stretch = "stretch",
  }

/** Vertikální zarovnání komponenty */
export enum JustifySelf {
start = "start",
end = "end",
center = "center",
stretch = "stretch",
}

/** Určuje, zdali si položky GridLayoutu zachovají výšku z definice, nebo se roztáhnou na výšku řádku. */
export enum GridItemsHeight {
    /** Výšky položek budou zachovány. */
    keepHeight = "keepHeight",
    /** Položky se roztáhnou na výšku řádku. Ta je určena nejvyšší položkou. */
    takeHighest = "takeHighest",
}

export type GridLayoutItemProps = {
    /** Potomci */
    children?: ReactNode;
    /** Šířka itemu v pixelech */
    width?: number | string;
    /** Výška itemu v pixelech */
    height?: number | string;
    /** Horizontální zarovnání položky */
    alignSelf?: AlignSelf;
    /** Vertikální zarovnání položky */
    justifySelf?: JustifySelf;
    /** Počet sloupců, které komponenta zabírá */
    colSpan?: ColSpan;
    /** Počet sloupců, které komponenta zabírá při šířce obrazovky S */
    colSpanS?: ColSpan;
    /** Počet sloupců, které komponenta zabírá při šířce obrazovky M */
    colSpanM?: ColSpan;
    /** Počet sloupců, které komponenta zabírá při šířce obrazovky L */
    colSpanL?: ColSpan;
    /** Počet sloupců, které komponenta zabírá při šířce obrazovky XL */
    colSpanXl?: ColSpan;
    /** Počet sloupců, které komponenta zabírá při šířce obrazovky XXL */
    colSpanXxl?: ColSpan;
    /** Počet řádků, které komponenta zabírá */
    rowSpan?: number;
    /** Počet řádků, které komponenta zabírá při šířce obrazovky S */
    rowSpanS?: number;
    /** Počet řádků, které komponenta zabírá při šířce obrazovky M */
    rowSpanM?: number;
    /** Počet řádků, které komponenta zabírá při šířce obrazovky L */
    rowSpanL?: number;
    /** Počet řádků, které komponenta zabírá při šířce obrazovky XL */
    rowSpanXl?: number;
    /** Počet řádků, které komponenta zabírá při šířce obrazovky XXL */
    rowSpanXxl?: number;
    /** Určuje pořadí v gridu */
    order?: number;
    /** Určuje viditelnost itemu */
    visible?: boolean;
};

const classNames = bemClassNames("grid-layout-item-component");

/** Komponenta umožňující grid rozložení */
export const GridLayoutItem: ForwardRefType<HTMLDivElement, GridLayoutItemProps> = forwardRef<HTMLDivElement, GridLayoutItemProps>(({
    children,
    width,
    height,
    alignSelf,
    justifySelf,
    colSpan,
    colSpanS,
    colSpanM,
    colSpanL,
    colSpanXl,
    colSpanXxl,
    rowSpan,
    rowSpanS,
    rowSpanM,
    rowSpanL,
    rowSpanXl,
    rowSpanXxl,
    order,
    visible = true,
}, ref) => {
    return (
        <div
            className={classNames(
                transformKeysToParamCase({
                    alignSelf,
                    justifySelf,
                    hidden: !visible,
                }),
            )}
            ref={ref}
            style={{
                ...transformKeysToParamCase({
                    colSpan,
                    colSpanS,
                    colSpanM,
                    colSpanL,
                    colSpanXl,
                    colSpanXxl,
                    rowSpan,
                    rowSpanS,
                    rowSpanM,
                    rowSpanL,
                    rowSpanXl,
                    rowSpanXxl,
                }, CUSTOM_CSS_PROPERTY_PREFIX),
                order,
                width,
                height,
            }}
        >
            {children}
        </div>
    );
});
